import React, { useState, useEffect } from "react";
import { Form, Input, Button, message, Spin, Typography } from "antd";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { getGlobalPrivacyPolicy, createGlobalPrivacyPolicy, updateGlobalPrivacyPolicy } from "../../../../api/privacyPolicy";

const { Title } = Typography;

const PrivacyPolicy = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [privacyData, setPrivacyData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  // Rich text editor modules configuration
  const modules = {
    toolbar: [
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ indent: "-1" }, { indent: "+1" }],
      [{ align: [] }],
      [{ color: [] }, { background: [] }],
      ["link"],
      ["clean"],
    ],
  };

  // Fetch existing privacy policy on component mount
  useEffect(() => {
    fetchPrivacyPolicy();
  }, []);

  const fetchPrivacyPolicy = async () => {
    setLoading(true);
    try {
      const data = await getGlobalPrivacyPolicy();
      console.log("Fetched privacy policy data:", data);
      if (data) {
        setPrivacyData(data);
        form.setFieldsValue({
          title: data.title,
          description: data.description,
          privacyPolicy: data.privacyPolicy,
        });
      }
    } catch (error) {
      console.error("Failed to fetch privacy policy:", error);
      message.error("Failed to load privacy policy");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    console.log("Form values:", values);
    setLoading(true);
    try {
      let data;
      
      if (privacyData) {
        // Update existing privacy policy
        data = await updateGlobalPrivacyPolicy(values);
        message.success("Privacy policy updated successfully");
      } else {
        // Create new privacy policy
        data = await createGlobalPrivacyPolicy(values);
        message.success("Privacy policy created successfully");
      }
      await fetchPrivacyPolicy();
      setIsEditing(false);
    } catch (error) {
      console.error("Failed to save privacy policy:", error);
      message.error("Failed to save privacy policy");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    if (privacyData) {
      form.setFieldsValue({
        title: privacyData.title,
        description: privacyData.description,
        privacyPolicy: privacyData.privacyPolicy,
      });
    } else {
      form.resetFields();
    }
    setIsEditing(false);
  };

  const renderPreview = () => {
    if (!privacyData) {
      return (
        <div className="text-center p-8">
          <p>No privacy policy has been created yet.</p>
          <Button type="primary" onClick={() => setIsEditing(true)}>
            Create Privacy Policy
          </Button>
        </div>
      );
    }

    return (
      <div className="p-4">
        <div className="flex justify-between items-center mb-4">
          <Title level={4}>{privacyData.title}</Title>
          <Button type="primary" onClick={handleEdit}>
            Edit
          </Button>
        </div>
        
        {/* Preview that matches customer app UI */}
        <div className="mb-6">
          <Title level={5}>Description</Title>
          <div 
            className="border p-4 rounded-md bg-gray-50 text-[15px] font-light leading-snug"
            dangerouslySetInnerHTML={{ __html: privacyData.description }} 
          />
        </div>
        
        <div className="mb-6">
          <Title level={5}>Privacy Policy</Title>
          <div className="border rounded-3xl bg-white p-6 shadow-sm">
            <div 
              className="text-[15px] font-light leading-snug"
              dangerouslySetInnerHTML={{ __html: privacyData.privacyPolicy }} 
            />
          </div>
        </div>
        
        <div className="mt-8">
          <Title level={5}>Preview</Title>
          <div className="border p-2 rounded-lg bg-gray-50">
            <div className="max-w-6xl mx-auto">
              <h1 className="text-xl sm:text-4xl font-normal leading-snug text-center text-black mb-6">
                {privacyData.title}
              </h1>
              
              <div className="w-full px-3 sm:px-3">
                <div 
                  className="text-[12px] sm:text-[14px] md:text-lg font-light text-center text-black max-w-5xl mx-auto"
                  dangerouslySetInnerHTML={{ __html: privacyData.description }}
                />
              </div>
              
              <div className="flex flex-col gap-3 leading-6 text-black md:bg-white rounded-3xl w-full px-3 sm:px-6 pb-6 md:pt-6 md:border border-[#89878E]">
                <div 
                  className="text-[12px] sm:text-[14px] md:text-[15px] leading-snug font-light"
                  dangerouslySetInnerHTML={{ __html: privacyData.privacyPolicy }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderForm = () => {
    return (
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        className="p-4"
      >
        <Form.Item
          name="title"
          label="Title"
          rules={[{ required: true, message: "Please enter a title" }]}
        >
          <Input placeholder="Privacy Policy" />
        </Form.Item>

        <Form.Item
          name="description"
          label="Description"
          extra="This will appear at the top of the Privacy Policy page as an introduction."
          rules={[{ required: true, message: "Please enter a description" }]}
        >
          <ReactQuill
            theme="snow"
            modules={modules}
            placeholder="Enter a brief description of the privacy policy..."
            style={{ minHeight: '200px' }}
          />
        </Form.Item>

        <Form.Item
          name="privacyPolicy"
          label="Privacy Policy Content"
          extra="This will appear as the main content of the Privacy Policy page. Use headings to organize sections."
          rules={[{ required: true, message: "Please enter privacy policy content" }]}
        >
          <ReactQuill
            theme="snow"
            modules={modules}
            placeholder="Enter the full privacy policy content..."
            style={{ minHeight: '400px' }}
          />
        </Form.Item>

        <Form.Item>
          <div className="flex justify-end space-x-2">
            <Button onClick={handleCancel}>Cancel</Button>
            <Button type="primary" htmlType="submit">
              Save
            </Button>
          </div>
        </Form.Item>
      </Form>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow">
      {isEditing ? renderForm() : renderPreview()}
    </div>
  );
};

export default PrivacyPolicy;
